import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";

import { functions as firebaseFunctions } from "../../firebaseConfig";
import {
  calculateRoundedUsedHours,
  fetchOldestSessionLogStart,
  fetchSessionLogsByDateRange,
  fetchSessionLogsForStudent,
  toDateMaybe,
} from "../tutor/session_logs/sessionLogApi";
import { fetchAllStudents } from "../admin/scheduling/schedulingApi";
import ProgressReportWorkspace from "./ProgressReportWorkspace";
import SessionLogWorkspace from "./SessionLogWorkspace";

import "./SessionLogsWorkspace.css";

const CHART_COLORS = [
  "#2563eb",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#6366f1",
];

const SESSION_LOG_WINDOW_DAYS = 14;
const SESSION_LOG_WINDOW_MS = SESSION_LOG_WINDOW_DAYS * 24 * 60 * 60 * 1000;

function formatDateInputKey(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateMedium(value) {
  const d = toDateMaybe(value);
  if (!d) return "-";
  return d.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatTimeShort(value) {
  const d = toDateMaybe(value);
  if (!d) return "-";
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function displayOrDash(value) {
  const text = String(value == null ? "" : value).trim();
  return text || "-";
}

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

function numberOrDash(value) {
  if (!Number.isFinite(value)) return "-";
  return String(value);
}

function joinTimeRange(log) {
  const start = formatTimeShort(log?.sessionStart);
  const end = formatTimeShort(log?.sessionEnd);

  if (start === "-" && end === "-") return "-";
  if (start === "-") return end;
  if (end === "-") return start;
  return `${start} - ${end}`;
}

function getUsedHours(log) {
  const used = toFiniteNumber(log?.usedHours);
  if (used != null && used >= 0) return used;
  return calculateRoundedUsedHours(log?.sessionStart, log?.sessionEnd);
}

function getSessionDateKey(log) {
  if (log?.sessionDateKey) return String(log.sessionDateKey);
  return formatDateInputKey(log?.sessionStart);
}

function sortLogsNewestFirst(rows = []) {
  return [...rows].sort((a, b) => {
    const aMs = toDateMaybe(a?.sessionStart)?.getTime() || 0;
    const bMs = toDateMaybe(b?.sessionStart)?.getTime() || 0;
    if (aMs !== bMs) return bMs - aMs;

    const aUpdated = toDateMaybe(a?.updatedAt)?.getTime() || 0;
    const bUpdated = toDateMaybe(b?.updatedAt)?.getTime() || 0;
    return bUpdated - aUpdated;
  });
}

function mergeLogsById(existingRows = [], nextRows = []) {
  const map = new Map();
  existingRows.forEach((row) => {
    if (row?.id) map.set(row.id, row);
  });
  nextRows.forEach((row) => {
    if (row?.id) map.set(row.id, row);
  });
  return sortLogsNewestFirst([...map.values()]);
}

function statusTone(statusRaw) {
  const status = String(statusRaw || "").toLowerCase();
  if (status === "completed") return { bg: "#ecfdf5", text: "#166534", border: "#bbf7d0" };
  if (status.includes("partial")) return { bg: "#fffbeb", text: "#b45309", border: "#fde68a" };
  if (status.includes("not done")) return { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" };
  return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
}

function toStringList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/\n|•|-/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function renderAiBullets(items = []) {
  if (!items.length) return <li>No highlights generated yet.</li>;
  return items.map((item, index) => <li key={`${item}_${index}`}>{item}</li>);
}

function SearchableDropdown({
  label,
  value,
  options,
  onChange,
  placeholder,
  id,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = String(value || "").trim().toLowerCase();
    if (!q) return options.slice(0, 80);
    return options
      .filter((option) => option.toLowerCase().includes(q))
      .slice(0, 80);
  }, [options, value]);

  return (
    <div className="sl-filterControl sl-searchableWrap">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          window.setTimeout(() => setIsOpen(false), 120);
        }}
        placeholder={placeholder}
        autoComplete="off"
      />

      {isOpen && (
        <div className="sl-searchMenu">
          {!filtered.length && <div className="sl-searchEmpty">No students found</div>}
          {filtered.map((option) => (
            <button
              type="button"
              className="sl-searchOption"
              key={option}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectHoursPieChart({ categoryHours }) {
  const entries = Object.entries(categoryHours || {})
    .filter(([, hours]) => Number.isFinite(Number(hours)) && Number(hours) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));

  if (!entries.length) {
    return <div className="sl-emptyChart">No hours yet in this range.</div>;
  }

  const total = entries.reduce((sum, [, hours]) => sum + Number(hours), 0);
  const size = 180;
  const center = size / 2;
  const radius = center - 10;

  let slices = [];

  if (entries.length === 1) {
    const [label, value] = entries[0];
    slices = [{
      label,
      hours: Number(value),
      pct: 1,
      color: CHART_COLORS[0],
      type: "full",
    }];
  } else {
    let cumulative = 0;

    slices = entries.map(([label, value], index) => {
      const hours = Number(value);
      const pct = total > 0 ? hours / total : 0;
      const startPct = cumulative;
      cumulative += pct;
      const endPct = cumulative;

      const startX = Math.cos(2 * Math.PI * startPct);
      const startY = Math.sin(2 * Math.PI * startPct);
      const endX = Math.cos(2 * Math.PI * endPct);
      const endY = Math.sin(2 * Math.PI * endPct);
      const largeArc = pct > 0.5 ? 1 : 0;

      const d = [
        `M ${center} ${center}`,
        `L ${center + radius * startX} ${center + radius * startY}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${center + radius * endX} ${center + radius * endY}`,
        "Z",
      ].join(" ");

      return {
        label,
        hours,
        pct,
        color: CHART_COLORS[index % CHART_COLORS.length],
        type: "slice",
        d,
      };
    });
  }

  return (
    <div className="sl-chartWrap">
      <svg viewBox={`0 0 ${size} ${size}`} width="170" height="170" className="sl-pieSvg">
        {slices.map((slice) => {
          if (slice.type === "full") {
            return (
              <circle
                key={slice.label}
                cx={center}
                cy={center}
                r={radius}
                fill={slice.color}
                stroke="#ffffff"
                strokeWidth="2"
              />
            );
          }

          return (
            <path
              key={slice.label}
              d={slice.d}
              fill={slice.color}
              stroke="#ffffff"
              strokeWidth="2"
            />
          );
        })}
      </svg>

      <div className="sl-chartLegend">
        {slices.map((slice) => (
          <div className="sl-chartLegendItem" key={`legend_${slice.label}`}>
            <span className="sl-chartDot" style={{ backgroundColor: slice.color }} />
            <span className="sl-chartLabel">{slice.label}</span>
            <span className="sl-chartValue">{Math.round(slice.pct * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionLogPage({
  logs,
  onShowMore,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
  loadedRangeLabel = "",
}) {
  const [studentQuery, setStudentQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTutor, setSelectedTutor] = useState("");

  const studentOptions = useMemo(() => {
    const set = new Set();
    logs.forEach((log) => {
      const name = String(log?.studentName || "").trim();
      if (name) set.add(name);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const tutorOptions = useMemo(() => {
    const set = new Set();
    logs.forEach((log) => {
      const name = String(log?.tutorName || "").trim();
      if (name) set.add(name);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const studentNeedle = studentQuery.trim().toLowerCase();

    return logs.filter((log) => {
      const studentName = String(log?.studentName || "").toLowerCase();
      const tutorName = String(log?.tutorName || "");
      const dateKey = getSessionDateKey(log);

      const matchesStudent = !studentNeedle || studentName.includes(studentNeedle);
      const matchesTutor = !selectedTutor || tutorName === selectedTutor;
      const matchesDate = !selectedDate || dateKey === selectedDate;

      return matchesStudent && matchesTutor && matchesDate;
    });
  }, [logs, selectedDate, selectedTutor, studentQuery]);

  return (
    <div className="sl-sectionCard">
      <div className="sl-formGrid sl-formGrid--log">
        <SearchableDropdown
          id="sl-log-student"
          label="Search Student"
          value={studentQuery}
          options={studentOptions}
          onChange={setStudentQuery}
          placeholder="Type and pick a student"
        />

        <div className="sl-filterControl">
          <label htmlFor="sl-log-date">Filter by Date</label>
          <input
            id="sl-log-date"
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </div>

        <div className="sl-filterControl">
          <label htmlFor="sl-log-tutor">Filter by Tutor</label>
          <select
            id="sl-log-tutor"
            value={selectedTutor}
            onChange={(event) => setSelectedTutor(event.target.value)}
          >
            <option value="">All Tutors</option>
            {tutorOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {loadedRangeLabel ? (
        <div className="sl-windowInfo">Loaded range: {loadedRangeLabel}</div>
      ) : null}

      <div className="sl-tableWrap">
        <table className="sl-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Student Name</th>
              <th>Tutor Name</th>
              <th className="sl-actionsCol">Action</th>
            </tr>
          </thead>
          <tbody>
            {!filteredLogs.length && (
              <tr>
                <td colSpan={6} className="sl-emptyRow">No session logs found with the current filters.</td>
              </tr>
            )}

            {filteredLogs.map((log) => (
              <tr key={log.id}>
                <td>{formatDateMedium(log?.sessionStart || log?.sessionDateKey)}</td>
                <td>{formatTimeShort(log?.sessionStart)}</td>
                <td>{formatTimeShort(log?.sessionEnd)}</td>
                <td>{displayOrDash(log?.studentName)}</td>
                <td>{displayOrDash(log?.tutorName)}</td>
                <td className="sl-actionsCell">
                  <button type="button" className="sl-btn sl-btnPrimary" onClick={() => onShowMore(log)}>
                    Show More
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sl-loadMoreRow">
        <button
          type="button"
          className="sl-btn"
          onClick={onLoadMore}
          disabled={loadingMore || !hasMore}
        >
          {loadingMore ? "Loading..." : hasMore ? "Load More (Older 2 Weeks)" : "No Older Logs"}
        </button>
      </div>
    </div>
  );
}

function SessionCard({ log, onShowMore }) {
  const ratings = log?.ratings || {};
  const tone = statusTone(log?.homeworkStatus);

  return (
    <div className="sl-sessionCard">
      <div className="sl-sessionHeader">
        <div>
          <h4>{formatDateMedium(log?.sessionStart || log?.sessionDateKey)}</h4>
          <div className="sl-sessionTutor">Tutor: {displayOrDash(log?.tutorName)}</div>
        </div>

        <div className="sl-sessionHeaderRight">
          <span className="sl-subjectBadge">{displayOrDash(log?.subject)}</span>
          <button type="button" className="sl-btn" onClick={() => onShowMore(log)}>Show More</button>
        </div>
      </div>

      <div className="sl-statsStrip">
        <div className="sl-statItem">
          <span className="sl-statLabel">Time</span>
          <span className="sl-statValue">{joinTimeRange(log)}</span>
        </div>

        <div className="sl-statItem">
          <span className="sl-statLabel">Homework Status</span>
          <span
            className="sl-statusBadge"
            style={{
              backgroundColor: tone.bg,
              color: tone.text,
              borderColor: tone.border,
            }}
          >
            {displayOrDash(log?.homeworkStatus)}
          </span>
        </div>

        <div className="sl-statItem">
          <span className="sl-statLabel">Effort</span>
          <span className="sl-statValue">{numberOrDash(toFiniteNumber(ratings?.effort))}</span>
        </div>

        <div className="sl-statItem">
          <span className="sl-statLabel">Motivation / Behavior / Focus</span>
          <span className="sl-statValue">
            {`${numberOrDash(toFiniteNumber(ratings?.motivation))} / ${numberOrDash(toFiniteNumber(ratings?.behavior))} / ${numberOrDash(toFiniteNumber(ratings?.focus))}`}
          </span>
        </div>
      </div>

      <div className="sl-sessionBody">
        {String(log?.homeworkComments || "").trim() && (
          <div className="sl-callout sl-calloutWarning">
            <span className="sl-sectionTitle">Homework Comments</span>
            <p>{log.homeworkComments}</p>
          </div>
        )}

        <div>
          <span className="sl-sectionTitle">Lesson Activity</span>
          <p>{displayOrDash(log?.lessonActivity)}</p>
        </div>

        <div className="sl-callout sl-calloutPrimary">
          <span className="sl-sectionTitle">Recommended Next Steps</span>
          <p>{displayOrDash(log?.nextFocus || log?.ai?.nextSessionPlan)}</p>
        </div>
      </div>
    </div>
  );
}

function buildProgressFallback(filteredLogs = []) {
  const ratings = filteredLogs
    .map((log) => log?.ratings || {})
    .map((row) => ({
      effort: toFiniteNumber(row.effort),
      motivation: toFiniteNumber(row.motivation),
      behavior: toFiniteNumber(row.behavior),
      focus: toFiniteNumber(row.focus),
    }));

  const avg = (key) => {
    const values = ratings.map((row) => row[key]).filter((value) => Number.isFinite(value) && value > 0);
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  };

  const completionCount = filteredLogs.filter(
    (log) => String(log?.homeworkStatus || "").toLowerCase() === "completed"
  ).length;

  const completionRate = filteredLogs.length
    ? Math.round((completionCount / filteredLogs.length) * 100)
    : 0;

  const strengths = [];
  if (avg("effort") != null && avg("effort") >= 4) strengths.push("Consistent effort across recent sessions.");
  if (avg("focus") != null && avg("focus") >= 4) strengths.push("Session focus has remained stable and strong.");
  if (completionRate >= 70) strengths.push("Homework completion is trending positively.");
  if (!strengths.length) strengths.push("The student is participating and showing gradual progress.");

  const improvements = [];
  if (completionRate < 70) improvements.push("Homework consistency should be improved with weekly follow-up.");
  if (avg("motivation") != null && avg("motivation") < 3.5) improvements.push("Motivation needs clearer short-term goals and reinforcement.");
  if (avg("behavior") != null && avg("behavior") < 3.5) improvements.push("Behavior consistency should be monitored more closely.");
  if (!improvements.length) improvements.push("Continue strengthening independent practice quality.");

  return {
    provider: "local_fallback",
    riskLevel: completionRate < 50 ? "Needs Attention" : "On Track",
    overallProgress: `Review covers ${filteredLogs.length} sessions. Homework completion is ${completionRate}% in this period.`,
    keyStrengths: strengths,
    areasForImprovement: improvements,
    mindsetAndStrategy: [
      "Close each session with one measurable checkpoint for the next meeting.",
      "Use short reflection prompts to improve student self-awareness.",
    ],
    focusAndMotivation: [
      "Balance challenge questions with confidence-building warmups.",
      "Track one focus goal per session and review at the end.",
    ],
    recommendation: [
      "Continue targeted concept review and monitor trend changes weekly.",
      "Reassess goals after the next 4 to 6 sessions.",
    ],
  };
}

function buildAggregatePayload(filteredLogs = [], studentName, startDate, endDate) {
  const listText = (extractor) => {
    return filteredLogs
      .map(extractor)
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 10)
      .join(" ");
  };

  const values = (key) => {
    return filteredLogs
      .map((log) => toFiniteNumber(log?.ratings?.[key]))
      .filter((value) => Number.isFinite(value));
  };

  const avgRating = (key) => {
    const nums = values(key);
    if (!nums.length) return null;
    return Math.round((nums.reduce((sum, num) => sum + num, 0) / nums.length) * 10) / 10;
  };

  const subjects = [...new Set(
    filteredLogs.map((log) => String(log?.subject || "").trim()).filter(Boolean)
  )];

  const severeFlag = filteredLogs
    .map((log) => String(log?.studentFlag || "").toLowerCase())
    .reduce((result, current) => {
      if (current === "at risk") return "At Risk";
      if (current === "needs attention" && result !== "At Risk") return "Needs Attention";
      return result;
    }, "On Track");

  return {
    subject: subjects.join(", ") || "General",
    topicCovered: `Student: ${studentName}. Date range: ${startDate || "N/A"} to ${endDate || "N/A"}.`,
    lessonActivity:
      listText((log) => log?.lessonActivity) ||
      listText((log) => log?.ai?.sessionSummary) ||
      "Reviewed session activity across selected logs.",
    learningInsight:
      listText((log) => log?.learningInsight) ||
      "Analyzed understanding, patterns, and recurring challenges from recent sessions.",
    nextFocus:
      listText((log) => log?.nextFocus) ||
      listText((log) => log?.ai?.nextSessionPlan) ||
      "Prioritize continuity, core weak areas, and measurable next-session goals.",
    homeworkGiven: listText((log) => log?.homeworkGiven),
    studentFlag: severeFlag,
    effort: avgRating("effort"),
    motivation: avgRating("motivation"),
    behavior: avgRating("behavior"),
    focus: avgRating("focus"),
    confidence: avgRating("confidence"),
  };
}

// eslint-disable-next-line no-unused-vars
function ProgressReportPage({ onShowMore, initialStudentName, refreshToken = 0 }) {
  const [studentQuery, setStudentQuery] = useState("");
  const [appliedSeed, setAppliedSeed] = useState("");
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentLogs, setStudentLogs] = useState([]);
  const [studentLogsLoading, setStudentLogsLoading] = useState(false);
  const [studentLogsError, setStudentLogsError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiReport, setAiReport] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchAllStudents();
        if (!alive) return;
        const names = [...new Set(
          rows
            .map((row) => String(row?.name || "").trim())
            .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));
        setStudentOptions(names);
      } catch (error) {
        console.error("Failed to load student names for progress report", error);
        if (alive) setStudentOptions([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshToken]);

  useEffect(() => {
    if (!initialStudentName || appliedSeed === initialStudentName) return;

    const exact = studentOptions.find(
      (name) => name.toLowerCase() === String(initialStudentName).trim().toLowerCase()
    );
    if (exact) {
      setStudentQuery(exact);
      setAppliedSeed(initialStudentName);
    }
  }, [appliedSeed, initialStudentName, studentOptions]);

  const selectedStudent = useMemo(() => {
    const needle = String(studentQuery || "").trim().toLowerCase();
    if (!needle) return "";
    const exact = studentOptions.find((name) => name.toLowerCase() === needle);
    return exact || "";
  }, [studentOptions, studentQuery]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selectedStudent) {
        if (alive) {
          setStudentLogs([]);
          setStudentLogsError("");
        }
        return;
      }

      setStudentLogsLoading(true);
      setStudentLogsError("");
      try {
        const rows = await fetchSessionLogsForStudent(selectedStudent, { maxRows: 4000 });
        if (!alive) return;
        setStudentLogs(sortLogsNewestFirst(rows));
      } catch (error) {
        console.error("Failed to load logs for selected student", error);
        if (!alive) return;
        setStudentLogs([]);
        setStudentLogsError("Could not load this student's session logs.");
      } finally {
        if (alive) setStudentLogsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshToken, selectedStudent]);

  useEffect(() => {
    if (!studentLogs.length) {
      setStartDate("");
      setEndDate("");
      setAiReport(null);
      setAiError("");
      return;
    }

    const ordered = sortLogsNewestFirst(studentLogs);
    setStartDate(formatDateInputKey(ordered[ordered.length - 1]?.sessionStart));
    setEndDate(formatDateInputKey(ordered[0]?.sessionStart));
    setAiReport(null);
    setAiError("");
  }, [studentLogs]);

  const filteredLogs = useMemo(() => {
    const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endMs = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;

    return sortLogsNewestFirst(studentLogs).filter((log) => {
      const sessionDate = toDateMaybe(log?.sessionStart);
      if (!sessionDate) return false;

      const ms = sessionDate.getTime();
      if (startMs != null && ms < startMs) return false;
      if (endMs != null && ms > endMs) return false;
      return true;
    });
  }, [endDate, startDate, studentLogs]);

  const subjectHours = useMemo(() => {
    const map = {};
    filteredLogs.forEach((log) => {
      const subject = String(log?.subject || "General").trim() || "General";
      map[subject] = (map[subject] || 0) + getUsedHours(log);
    });
    return map;
  }, [filteredLogs]);

  const subjectEntries = useMemo(() => {
    return Object.entries(subjectHours).sort((a, b) => Number(b[1]) - Number(a[1]));
  }, [subjectHours]);

  const totalHours = useMemo(() => {
    return filteredLogs.reduce((sum, log) => sum + getUsedHours(log), 0);
  }, [filteredLogs]);

  const handleGenerateAi = async () => {
    if (!filteredLogs.length || !selectedStudent) {
      setAiError("Pick a valid student and date range with at least one session.");
      return;
    }

    setAiError("");
    setAiLoading(true);

    const fallback = buildProgressFallback(filteredLogs);

    try {
      const payload = buildAggregatePayload(filteredLogs, selectedStudent, startDate, endDate);
      const callable = httpsCallable(firebaseFunctions, "generateSessionLogAi");
      const response = await callable({ mode: "generate_all", payload });
      const data = response?.data || {};

      setAiReport({
        ...fallback,
        provider: data?.provider || "gemini",
        overallProgress: String(data?.sessionSummary || fallback.overallProgress).trim(),
        mindsetAndStrategy: [
          ...toStringList(data?.homeworkAssigned),
          ...fallback.mindsetAndStrategy,
        ].slice(0, 4),
        recommendation: [
          ...toStringList(data?.nextSessionPlan),
          ...toStringList(data?.riskAlert),
          ...fallback.recommendation,
        ].slice(0, 5),
      });
    } catch (error) {
      console.error("Failed to generate AI progress report", error);
      setAiError("AI report could not be generated right now. Showing a local summary instead.");
      setAiReport(fallback);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="sl-reportLayout">
      <div className="sl-sectionCard">
        <div className="sl-formGrid sl-formGrid--report">
          <SearchableDropdown
            id="sl-report-student"
            label="Search Student"
            value={studentQuery}
            options={studentOptions}
            onChange={setStudentQuery}
            placeholder="Type and pick a student"
          />

          <div className="sl-filterControl">
            <label htmlFor="sl-report-start">Start Date</label>
            <input
              id="sl-report-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className="sl-filterControl">
            <label htmlFor="sl-report-end">End Date</label>
            <input
              id="sl-report-end"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>

          <div className="sl-filterControl sl-filterControl--button">
            <label>AI Summary</label>
            <button
              type="button"
              className="sl-btn sl-btnPrimary sl-btnWide"
              onClick={handleGenerateAi}
              disabled={aiLoading || !filteredLogs.length}
            >
              {aiLoading ? "Generating..." : "Generate AI Report"}
            </button>
            <div className="sl-filterHint">Sessions in range: <strong>{filteredLogs.length}</strong></div>
          </div>
        </div>

        {studentLogsError && <div className="sl-warningBox">{studentLogsError}</div>}
        {aiError && <div className="sl-warningBox">{aiError}</div>}
      </div>

      <div className="sl-sectionCard">
        <h3>Hours Summary</h3>
        <div className="sl-hoursGrid">
          <div>
            <div className="sl-hoursTotal">
              Total Hours Used: <span>{totalHours.toFixed(2)} hours</span>
            </div>
            <ul className="sl-hoursList">
              {!subjectEntries.length && <li>No subject hours found.</li>}
              {subjectEntries.map(([subject, hours]) => (
                <li key={subject}><strong>{subject}:</strong> {Number(hours).toFixed(2)} hours</li>
              ))}
            </ul>
          </div>

          <SubjectHoursPieChart categoryHours={subjectHours} />
        </div>
      </div>

      {aiReport && (
        <div className="sl-sectionCard">
          <h3>
            AI Progress Summary
            {aiReport.riskLevel ? <span className="sl-riskPill">{aiReport.riskLevel}</span> : null}
          </h3>
          {aiReport.provider ? <div className="sl-modelTag">Generated by {aiReport.provider}</div> : null}

          <div className="sl-aiSummaryBlock">
            <h4>Overall Progress / Overview</h4>
            <p>{displayOrDash(aiReport.overallProgress)}</p>

            <h4>Key Strengths</h4>
            <ul>{renderAiBullets(aiReport.keyStrengths)}</ul>

            <h4>Areas for Improvement</h4>
            <ul>{renderAiBullets(aiReport.areasForImprovement)}</ul>

            <h4>Mindset &amp; Strategy</h4>
            <ul>{renderAiBullets(aiReport.mindsetAndStrategy)}</ul>

            <h4>Focus &amp; Motivation</h4>
            <ul>{renderAiBullets(aiReport.focusAndMotivation)}</ul>

            <h4>Recommendation</h4>
            <ul>{renderAiBullets(aiReport.recommendation)}</ul>
          </div>
        </div>
      )}

      <div className="sl-reportSessions">
        <h3>Session Details</h3>
        {studentLogsLoading && <div className="sl-sectionCard">Loading student session logs...</div>}
        {!studentLogsLoading && !selectedStudent && (
          <div className="sl-sectionCard">Pick a student to load progress report logs.</div>
        )}
        {!studentLogsLoading && selectedStudent && !filteredLogs.length && (
          <div className="sl-sectionCard">No session logs found for this student/date range.</div>
        )}

        {!studentLogsLoading && filteredLogs.map((log) => (
          <SessionCard key={log.id} log={log} onShowMore={onShowMore} />
        ))}
      </div>
    </div>
  );
}

export default function SessionLogsWorkspace({ viewerRole = "admin", page = "log", initialStudentName = "" }) {
  const isAdminViewer = viewerRole === "admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [windowCursorEnd, setWindowCursorEnd] = useState(() => new Date());
  const [oldestLogStart, setOldestLogStart] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [refreshToken, setRefreshToken] = useState(0);

  const handleOpenNewTab = (log) => {
    localStorage.setItem("sessionLog_" + log.id, JSON.stringify(log));
    localStorage.setItem("tutorSession_" + log.id, JSON.stringify(log));
    window.open(`/session-detail?id=${log.id}`, "_blank");
  };

  const pageTitle = page === "progress" ? "Session Progress Report" : "Session Log";
  const pageDescription = page === "progress"
    ? "Analyze student progress with hours summary, timeline, and AI-generated overview."
    : isAdminViewer
      ? "Search, filter, and review detailed tutoring session logs."
      : "Review tutoring session logs with the latest entries loaded first.";

  const loadInitialLogWindow = useCallback(async () => {
    if (page !== "log") return;

    setLoading(true);
    setError("");
    setLoadingMore(false);
    setHasMore(false);
    setLogs([]);
    const initialEnd = new Date();
    setWindowCursorEnd(initialEnd);

    try {
      const oldestStart = await fetchOldestSessionLogStart();
      setOldestLogStart(oldestStart || null);

      const initialStart = new Date(initialEnd.getTime() - SESSION_LOG_WINDOW_MS);
      const rows = await fetchSessionLogsByDateRange({
        startDate: initialStart,
        endDate: initialEnd,
        maxRows: 1200,
      });
      setLogs(sortLogsNewestFirst(rows));

      setWindowCursorEnd(initialStart);
      if (oldestStart) {
        setHasMore(initialStart.getTime() > oldestStart.getTime());
      } else {
        setHasMore(false);
      }
    } catch (fetchError) {
      console.error("Failed to load session logs", fetchError);
      setLogs([]);
      setError("Could not load session logs right now.");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const loadMoreLogs = useCallback(async () => {
    if (page !== "log" || loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError("");
    try {
      const end = windowCursorEnd || new Date();
      const start = new Date(end.getTime() - SESSION_LOG_WINDOW_MS);
      const rows = await fetchSessionLogsByDateRange({
        startDate: start,
        endDate: end,
        maxRows: 1200,
      });

      setLogs((prev) => mergeLogsById(prev, rows));
      setWindowCursorEnd(start);

      if (oldestLogStart) {
        setHasMore(start.getTime() > oldestLogStart.getTime());
      } else {
        setHasMore(rows.length > 0);
      }
    } catch (fetchError) {
      console.error("Failed to load older session logs", fetchError);
      setError("Could not load older session logs right now.");
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, oldestLogStart, page, windowCursorEnd]);

  useEffect(() => {
    if (page === "log") {
      loadInitialLogWindow();
      return;
    }
    setLoading(false);
    setLogs([]);
    setError("");
  }, [loadInitialLogWindow, page]);

  const loadedRangeLabel = useMemo(() => {
    if (!logs.length) return "";
    const ordered = sortLogsNewestFirst(logs);
    const newest = toDateMaybe(ordered[0]?.sessionStart);
    const oldest = toDateMaybe(ordered[ordered.length - 1]?.sessionStart);
    if (!newest || !oldest) return "";
    return `${formatDateMedium(oldest)} - ${formatDateMedium(newest)}`;
  }, [logs]);

  const handleRefresh = useCallback(() => {
    if (page === "log") {
      loadInitialLogWindow();
      return;
    }
    setRefreshToken((prev) => prev + 1);
  }, [loadInitialLogWindow, page]);


  // Progress page — fully independent workspace
  if (page === "progress") return <ProgressReportWorkspace />;

  // Session log page — fully redesigned workspace
  if (page === "log") return <SessionLogWorkspace viewerRole={viewerRole} />;

  return (
    <div className="sl-shell">
      <div className="sl-pageHeader">
        <div>
          <h1>{pageTitle}</h1>
          <p>{pageDescription}</p>
        </div>

        <button type="button" className="sl-btn" onClick={handleRefresh} disabled={loading || loadingMore}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="sl-errorBox">{error}</div>}
      {loading && !logs.length && <div className="sl-sectionCard">Loading session logs...</div>}

      {!loading && !error && page === "log" && (
        <SessionLogPage
          logs={logs}
          onShowMore={handleOpenNewTab}
          onLoadMore={loadMoreLogs}
          loadingMore={loadingMore}
          hasMore={hasMore}
          loadedRangeLabel={loadedRangeLabel}
        />
      )}

    </div>
  );
}
